import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import { PlanType } from '@labrav/react-components'
import { AzureOfferPlan, AzurePricingType } from '../../types/flyout'
import {
  getContainerCustomMeters,
  getContainerPlanTcvValue,
  getMeterPricePerPaymentInUsd,
  hasAzureContainerSystemMeters,
  hasCustomMeteringDimensions,
} from '../../components/MicrosoftFormComponent/ContainerPlan/containerPlanUtils'
import { isNonEmptyNumber } from '@labrav/common'
import { emptyFieldMessage, missignFieldMessage } from './messagesContants'
import { microsoftOfferAndMPPOValidationSchemaCommon } from './validateMicrosoftResellerAuthorization'
import { buildOfferRecipientsValidation } from './offerRecipientYupValidation'

const priceValidationMessage = 'Enter value (max 5 decimals, ≤100,000,000).'

const isValidContainerPrice = (value?: number | null) => {
  if (value === null || value === undefined || !isNonEmptyNumber(value)) {
    return false
  }

  if (value <= 0 || value > 100000000) {
    return false
  }

  const decimalPlaces = (value.toString().split('.')[1] || '').length
  return decimalPlaces <= 5
}

const containerDiscountPercentageSchema = Yup.number()
  .nullable()
  .test('discount', 'Enter discount.', function (value) {
    return this.options?.context?.azurePricingType ===
      AzurePricingType.LABRA_PERCENTAGE
      ? !(value === null || value === undefined || !isNonEmptyNumber(value))
      : true
  })
  .test('discount-range', 'Enter valid discount.', function (value) {
    if (
      this.options?.context?.azurePricingType ===
        AzurePricingType.LABRA_PERCENTAGE &&
      value !== null &&
      value !== undefined &&
      isNonEmptyNumber(value)
    ) {
      return value >= 0 && value <= 100
    }
    return true
  })

const containerFilledPlanSchema = Yup.object().shape({
  labraPlanId: Yup.string().required('Select a plan.'),
  discountPercentage: containerDiscountPercentageSchema,
  pricing: Yup.object().test(
    'container-plan-pricing',
    priceValidationMessage,
    function (pricing) {
      const plan = this.parent as AzureOfferPlan
      if (!plan?.labraPlanId) {
        return true
      }

      if (hasCustomMeteringDimensions(plan)) {
        const customMeters = getContainerCustomMeters(plan)
        const errors: Yup.ValidationError[] = []

        Object.entries(customMeters).forEach(([meterKey, meter]) => {
          const price = getMeterPricePerPaymentInUsd(meter)
          if (!isValidContainerPrice(price)) {
            errors.push(
              this.createError({
                path: `${this.path}.customMeters.meters.${meterKey}.pricePerPaymentInUsd`,
                message: priceValidationMessage,
              })
            )
          }
        })

        if (!isEmpty(errors)) {
          throw new Yup.ValidationError(errors)
        }

        return true
      }

      if (
        hasAzureContainerSystemMeters(plan, { productType: PlanType.CONTAINER })
      ) {
        const tcv = getContainerPlanTcvValue(plan)
        if (!isValidContainerPrice(tcv)) {
          return this.createError({
            path: `${this.path}.recurrentPrice.prices[0].pricePerPaymentInUsd`,
            message: priceValidationMessage,
          })
        }
      }

      return true
    }
  ),
})

const containerPlansValidationSchema = Yup.array()
  .test('at-least-one-plan', 'Select a plan.', plans =>
    (plans || []).some(plan => !isEmpty(plan?.labraPlanId))
  )
  .of(
    Yup.lazy((plan: AzureOfferPlan) => {
      if (!plan?.labraPlanId) {
        return Yup.object().shape({})
      }

      return containerFilledPlanSchema
    }) as unknown as Yup.AnySchema
  )

const { plans: _plans, ...containerCommonSchemaFields } =
  microsoftOfferAndMPPOValidationSchemaCommon

const recipientMessages = {
  firstNameRequired: missignFieldMessage,
  lastNameRequired: missignFieldMessage,
  emailRequired: 'Please provide the email of your offer recipient. ',
  emailInvalid: 'Enter valid email.',
  duplicateEmail: 'Duplicate emails are not allowed.',
}

const containerMicrosoftPrivateOfferValidationSchema = (
  isSalesforceContext: boolean
) =>
  Yup.object().shape({
    ...containerCommonSchemaFields,
    plans: containerPlansValidationSchema,
    preparedBy: isSalesforceContext
      ? Yup.string()
          .required(emptyFieldMessage)
          .email('Enter a valid email address.')
      : Yup.string().nullable(),
    billingAccountId: Yup.string().required(emptyFieldMessage),
    privateOfferName: Yup.string().required(emptyFieldMessage),
    offerStakeholders: isSalesforceContext
      ? Yup.array().optional().notRequired()
      : Yup.array()
          .of(
            Yup.object().shape({
              email: Yup.string().required('Email cannot be empty.'),
            })
          )
          .min(1, 'At least one stakeholder is required.'),
    offerRecipients: buildOfferRecipientsValidation(recipientMessages),
  })

export default containerMicrosoftPrivateOfferValidationSchema
